import React from 'react';
import { X, Video, ExternalLink } from 'lucide-react';
interface StudentJoinLessonPanelProps {
  isOpen: boolean;
  onClose: () => void;
  lesson: {
    id: string;
    teacherName: string;
    meetingLink?: string;
  } | null;
  darkMode: boolean;
}
export const StudentJoinLessonPanel: React.FC<StudentJoinLessonPanelProps> = ({
  isOpen,
  onClose,
  lesson,
  darkMode
}) => {
  const handleOpenMeeting = () => {
    if (lesson?.meetingLink?.trim()) {
      // Add https:// if no protocol specified
      let url = lesson.meetingLink.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };
  if (!isOpen || !lesson) return null;
  const hasLink = !!lesson.meetingLink?.trim();
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={onClose} aria-hidden="true" data-id="element-2339" />

      {/* Slide-over Panel */}
      <div className={`fixed inset-y-0 right-0 w-full sm:w-[480px] z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-2340">
        <div className={`h-full flex flex-col rounded-l-2xl ${darkMode ? 'bg-navy-800' : 'bg-white'} shadow-2xl`} data-id="element-2341">
          {/* Header */}
          <div className={`flex items-center justify-between px-6 py-4 border-b ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-2342">
            <div className="flex items-center gap-3" data-id="element-2343">
              <div className={`p-2 rounded-lg ${darkMode ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-2344">
                <Video className={`h-5 w-5 ${darkMode ? 'text-green-400' : 'text-green-600'}`} data-id="element-2345" />
              </div>
              <div data-id="element-2346">
                <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2347">
                  Online Lesson
                </h2>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-2348">
                  with {lesson.teacherName}
                </p>
              </div>
            </div>
            <button onClick={onClose} className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-navy-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'}`} aria-label="Close panel" data-id="element-2349">
              <X className="h-5 w-5" data-id="element-2350" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-2351">
            <div className="space-y-6" data-id="element-2352">
              {/* Meeting Link Section */}
              <div data-id="element-2353">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2354">
                  Meeting Link
                </label>

                {hasLink ? <div className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} break-all`} data-id="element-2355">
                    {lesson.meetingLink}
                  </div> : <div className={`w-full px-4 py-4 rounded-lg border border-dashed text-center ${darkMode ? 'bg-navy-900/20 border-navy-700 text-gray-400' : 'bg-gray-50 border-gray-300 text-gray-500'}`} data-id="element-2356">
                    Meeting link not available yet.
                    <br data-id="element-2357" />
                    Your teacher will add it before the lesson.
                  </div>}
              </div>

              {/* Action Buttons */}
              <div className="space-y-3" data-id="element-2358">
                {/* Open Meeting Button - Unified Green Outline Style (Both Modes) */}
                <button onClick={handleOpenMeeting} disabled={!hasLink} className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors !bg-transparent !border-2 !border-green-500 !text-green-500 hover:!bg-green-500/10 disabled:!border-gray-400 disabled:!text-gray-400 disabled:cursor-not-allowed" data-id="element-2359">
                  <ExternalLink className={`h-4 w-4 ${hasLink ? '!text-green-500' : ''}`} data-id="element-2360" />
                  Open Meeting
                </button>
              </div>

              {/* Info Box */}
              {hasLink && <div className={`p-4 rounded-lg ${darkMode ? 'bg-navy-700/50' : 'bg-gray-50'}`} data-id="element-2361">
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-2362">
                    <strong className={darkMode ? 'text-gray-300' : 'text-gray-700'} data-id="element-2363">
                      Tip:
                    </strong>{' '}
                    The meeting will open in a new tab. You can return to this
                    dashboard anytime.
                  </p>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </>;
};